// UserContext.tsx

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import useFetch from '@/hooks/useFetch';
import { UserData } from '@/@types/common';
import i18next from 'i18next';
import { useAuthContext } from './AuthContext';

interface UserContextProps {
  userData: UserData | null;
  loading: boolean;
  error: any;
  refreshUserData: () => void;
}

const UserContext = createContext<UserContextProps>({
  userData: null,
  loading: true,
  error: null,
  refreshUserData: () => {},
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authenticated, refresh } = useAuthContext();
  const { data, loading, execute, status, error } = useFetch<any>({
    url: '/user/',
    method: 'GET',
    immediate: false,
  });

  const [userData, setUserData] = useState<UserData | null>(null);
  const lastFetchRef = useRef<number>(0);

  const refreshUserData = () => {
    const now = Date.now();
    if (!loading && (!userData || now - lastFetchRef.current > 300000)) {
      console.log("Fetching user data...");
      lastFetchRef.current = now;
      execute();
    } else {
      console.log("Skipping user data fetch - too soon or already loading");
    }
  };

  useEffect(() => {
    if (authenticated && !userData) {
      refreshUserData();
    }
  }, [authenticated]);

  useEffect(() => {
    if (status === 401) {
      console.error("Error fetching user data:", status);
    }
  }, [status]);

  useEffect(() => {
    if (!authenticated) {
      setUserData(null);
    }
  }, [authenticated]);

  useEffect(() => {
    if (data) {
      const userData = data.data;
      if (userData) {
        const userWithLang = {
          ...userData,
          lang: userData.lang.split("-")[0] || "en",
        };
        setUserData(userWithLang);
        localStorage.setItem("user_email", userWithLang.email);
        i18next.changeLanguage(userWithLang.lang);
      }
    } else if (status === 401) {
      refresh();
    }
  }, [data, status, refresh]);

  const contextValue = {
    userData,
    loading,
    error,
    refreshUserData,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
